import { useCallback, useState } from 'react'

import { ArrowRight01Icon, PlayIcon } from '@hugeicons/react'

import { KIND_LABEL, isVideoItem } from './catalogKinds'
import { PartnerStoriesViewer } from './PartnerStoriesViewer'

const seenKey = partnerId => `partner-catalog-seen:${partnerId}`

const readSeen = partnerId => {
  if (!partnerId || typeof window === 'undefined') return []
  try {
    return JSON.parse(window.localStorage.getItem(seenKey(partnerId)) || '[]')
  } catch {
    return []
  }
}

/**
 * Premium partner catalog (handoff bloco 5). Horizontal 9:16 story cards with a
 * gradient "story ring" (unseen) that turns neutral once opened, a type badge
 * and a chevron affordance. Tapping a card opens the full-screen stories viewer.
 *
 * Omitted entirely when there are no items (requirement 2.4); items without
 * media still render with a neutral fill (requirement 2.5).
 */
export const PartnerCatalog = ({ items = [], partnerId }) => {
  const [storyIndex, setStoryIndex] = useState(null)
  const [seen, setSeen] = useState(() => readSeen(partnerId))

  const markSeen = useCallback(
    id => {
      setSeen(prev => {
        if (prev.includes(id)) return prev
        const next = [...prev, id]
        if (partnerId && typeof window !== 'undefined') {
          try {
            window.localStorage.setItem(seenKey(partnerId), JSON.stringify(next))
          } catch {
            /* ignore quota / privacy-mode failures */
          }
        }
        return next
      })
    },
    [partnerId]
  )

  if (!items.length) return null

  return (
    <section>
      <div className="mb-1 flex items-baseline justify-between">
        <h2 className="m-0 text-[15px] font-bold text-gray-900">Catálogo</h2>
        <span className="text-xs text-gray-700">toque para ver · {items.length}</span>
      </div>

      <div className="-mx-3 flex gap-3 overflow-x-auto px-3 pb-1 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((item, index) => {
          const isSeen = seen.includes(item.id)
          const mediaUrl = item?.media?.file?.url
          const isVideo = isVideoItem(item)
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setStoryIndex(index)}
              className="w-[136px] shrink-0 text-left"
            >
              {/* 136×196 fixed box — keep in sync with CATALOG_MEDIA_ASPECT_CLASS
                  (catalogKinds.js), reused by the expanded viewer so a story
                  crops the same way in both places. */}
              <div
                className={`relative h-[196px] rounded-[18px] p-[3px] ${
                  isSeen
                    ? 'bg-[#E3DCF5]'
                    : 'bg-[linear-gradient(160deg,#8B5CF6,#C4B5FD)]'
                }`}
              >
                <div className="relative h-full overflow-hidden rounded-[15px] bg-brand-light">
                  {mediaUrl && (
                    <img src={mediaUrl} alt={item.title} className="h-full w-full object-cover" />
                  )}
                  {isVideo && (
                    <span className="absolute inset-0 grid place-items-center bg-black/25">
                      <span className="grid h-11 w-11 place-items-center rounded-full bg-white/90 text-gray-900">
                        <PlayIcon size={18} variant="solid" />
                      </span>
                    </span>
                  )}
                  <span className="absolute left-[7px] top-[7px] rounded-lg bg-white/95 px-[7px] py-[3px] text-[9.5px] font-bold text-brand">
                    {KIND_LABEL[item.kind] || item.kind}
                  </span>
                  <span className="absolute bottom-[7px] right-[7px] grid h-[26px] w-[26px] place-items-center rounded-full bg-[rgba(30,27,46,.6)] text-white">
                    <ArrowRight01Icon size={12} variant="stroke" />
                  </span>
                </div>
              </div>
              <p className="mt-2 mb-0 line-clamp-2 text-[13px] font-semibold leading-snug text-gray-900">
                {item.title}
              </p>
            </button>
          )
        })}
      </div>

      {storyIndex !== null && (
        <PartnerStoriesViewer
          items={items}
          initialIndex={storyIndex}
          onClose={() => setStoryIndex(null)}
          onSeen={markSeen}
        />
      )}
    </section>
  )
}
